import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu", "progress"]

  connect() {
    this.lastY = Math.max(0, window.scrollY)
    this.direction = 0
    this.travel = 0
    this.frame = null
    this.prepareNavigation()
    this.onScroll = () => {
      if (this.frame !== null) return
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null
        this.update()
      })
    }
    this.onResize = () => {
      if (window.matchMedia("(min-width: 1101px)").matches && this.menuOpen) {
        const focusedInside = this.menuTarget.contains(document.activeElement)
        this.menuTarget.open = false
        if (focusedInside) this.element.querySelector(".site-header-brand").focus({ preventScroll: true })
      }
      this.update()
    }
    this.onPointerDown = event => {
      if (!this.element.contains(event.target)) this.closeMenu()
    }
    this.onKeyDown = event => {
      if (event.key === "Escape" && this.menuOpen) {
        event.preventDefault()
        this.closeMenu()
        this.menuTarget.querySelector("summary").focus({ preventScroll: true })
      }
    }
    this.beforeCache = () => {
      this.closeMenu()
      this.setState("expanded")
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onResize)
    document.addEventListener("pointerdown", this.onPointerDown)
    document.addEventListener("keydown", this.onKeyDown)
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.setState(this.lastY > 80 ? "compact" : "expanded")
    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onResize)
    document.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("keydown", this.onKeyDown)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    if (this.frame !== null) window.cancelAnimationFrame(this.frame)
  }

  update() {
    const y = Math.max(0, window.scrollY)
    const delta = y - this.lastY
    this.lastY = y
    this.element.dataset.siteHeaderElevated = String(y > 8)
    this.updateNavigation()
    this.updateProgress(y)

    // Keep page layout stable while the surface shrinks; ignore trackpad jitter.
    if (y <= 80 || this.interacting) {
      this.direction = 0
      this.travel = 0
      this.setState("expanded")
      return
    }
    if (delta === 0) return
    const direction = Math.sign(delta)
    this.travel = direction === this.direction ? this.travel + Math.abs(delta) : Math.abs(delta)
    this.direction = direction
    if (this.travel >= 12) {
      this.setState(direction > 0 ? "compact" : "expanded")
      this.travel = 0
    }
  }

  prepareNavigation() {
    const current = new URL(window.location.href)
    const currentPath = this.normalizedPath(current.pathname)
    this.navigation = Array.from(this.element.querySelectorAll(".site-header-link")).map(link => {
      const url = new URL(link.href, current)
      const path = this.normalizedPath(url.pathname)
      const sameOrigin = url.origin === current.origin
      const samePage = sameOrigin && path === currentPath
      return {
        link,
        page: !url.hash && sameOrigin && (samePage || (path !== "/" && currentPath.startsWith(`${path}/`))),
        section: samePage && url.hash ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null
      }
    })
  }

  normalizedPath(path) {
    return path.replace(/\/+$/, "") || "/"
  }

  updateNavigation() {
    // Use the stable expanded height so a shrinking bar cannot change selection.
    const boundary = this.element.getBoundingClientRect().height + 32
    this.navigation.forEach(entry => {
      let current = entry.page ? "page" : null
      if (entry.section) {
        const rect = entry.section.getBoundingClientRect()
        if (rect.top <= boundary && rect.bottom > boundary) current = "location"
      }
      if (current === entry.current) return
      if (current) entry.link.setAttribute("aria-current", current)
      else entry.link.removeAttribute("aria-current")
      entry.current = current
    })
  }

  updateProgress(y) {
    if (!this.hasProgressTarget) return
    const distance = document.documentElement.scrollHeight - window.innerHeight
    this.progressTarget.value = distance > 0 ? Math.min(1, Math.max(0, y / distance)) : 0
  }

  get menuOpen() {
    return this.hasMenuTarget && this.menuTarget.open
  }

  get interacting() {
    const focused = document.activeElement
    return this.menuOpen || (focused && this.element.contains(focused) && focused.matches(":focus-visible"))
  }

  setState(state) {
    this.element.dataset.siteHeaderState = state
  }

  menuChanged() {
    this.update()
  }

  focusChanged() {
    this.onScroll()
  }

  navigate(event) {
    if (event.target.closest(".site-header-menu-panel a")) this.closeMenu()
  }

  closeMenu() {
    if (this.menuOpen) this.menuTarget.open = false
  }
}
